import { useState, type Dispatch, type SetStateAction } from 'react';

type Prettify<T> = {
    [K in keyof T]: T[K];
} & {};

type ReactiveState<T> = Prettify<{
    [K in keyof T]: T[K];
} & {
    [K in keyof T as `set${Capitalize<string & K>}`]: Dispatch<SetStateAction<T[K]>>;
}>;

type AllowedValues = string | number | boolean | null | undefined | object;

export function useReactiveState<T extends Record<string, AllowedValues>>(
    initialValues: T
): ReactiveState<T> {
    try {
        const [state, setState] = useState<T>(initialValues);
        type Setters = {
            [K in keyof T as `set${Capitalize<string & K>}`]: Dispatch<SetStateAction<T[K]>>;
        };

        const setters = Object.keys(initialValues).reduce<Partial<Setters>>((acc, key) => {
            const k = key as keyof T;
            const capitalizedKey = `set${k.toString().charAt(0).toUpperCase() + k.toString().slice(1)}` as keyof Setters;
            if (typeof state[k] === "function") {
                // @ts-expect-error only use for testing
                acc[k] = state[k] as object;
            } else {
                // @ts-expect-error only use for testing
                acc[capitalizedKey] = (newValue: SetStateAction<T[typeof k]>) => {
                    setState((prevState) => {
                        const newState = { ...prevState, [k]: newValue };
                        return newState;
                    });
                };
            }

            return acc;
        }, {} as Partial<Setters>);
        const newState = { ...state, ...setters } as ReactiveState<T>;
        return newState;
    } catch (error) {
        console.error('Error in useReactiveState:', error);
        return initialValues as ReactiveState<T>;
    }
}
